import type {
  BuildDto,
  BuildStatus,
  CiConclusion,
  CiLifecycleState,
  PipelineLatestRunDto,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
} from '@flowqa/shared';

export type CiDisplayStatus = BuildStatus | CiLifecycleState | CiConclusion;

type CiDisplayStatusInput = {
  status?: BuildStatus | null;
  lifecycleState?: CiLifecycleState | null;
  conclusion?: CiConclusion | null;
  requiresManualAction?: boolean | null;
};

export const resolvePreferredCiDisplayStatus = ({
  status,
  lifecycleState,
  conclusion,
  requiresManualAction,
}: CiDisplayStatusInput): CiDisplayStatus | null => {
  if (requiresManualAction === true) {
    return 'action_required';
  }
  if (conclusion) {
    return conclusion;
  }
  if (lifecycleState && lifecycleState !== 'completed') {
    return lifecycleState;
  }
  return status ?? lifecycleState ?? null;
};

export const resolveBuildDisplayStatus = (build: BuildDto): CiDisplayStatus | null =>
  resolvePreferredCiDisplayStatus({
    status: build.status,
    lifecycleState: build.lifecycleState,
    conclusion: build.conclusion,
    requiresManualAction: build.requiresManualAction,
  });

export const resolvePipelineLatestRunDisplayStatus = (
  latestRun: PipelineLatestRunDto | null | undefined,
): CiDisplayStatus | null =>
  resolvePreferredCiDisplayStatus({
    status: latestRun?.status,
    lifecycleState: latestRun?.lifecycleState,
    conclusion: latestRun?.conclusion,
    requiresManualAction: latestRun?.requiresManualAction,
  });

export const resolveStageDisplayStatus = (
  stage: PipelineStageListItemDto,
): CiDisplayStatus | null =>
  resolvePreferredCiDisplayStatus({
    status: stage.status,
    lifecycleState: stage.lifecycleState ?? stage.latestRun?.lifecycleState ?? null,
    conclusion: stage.conclusion ?? stage.latestRun?.conclusion ?? null,
    requiresManualAction:
      stage.requiresManualAction ?? stage.latestRun?.requiresManualAction ?? null,
  });

export const resolveStageExecutionDisplayStatus = (
  execution: PipelineStageExecutionListItemDto,
): CiDisplayStatus | null =>
  resolvePreferredCiDisplayStatus({
    status: execution.status,
    lifecycleState: execution.lifecycleState,
    conclusion: execution.conclusion,
    requiresManualAction: execution.requiresManualAction,
  });

export const resolveCiStatusLabelKey = (
  status: CiDisplayStatus | null,
): CiDisplayStatus | BuildStatus | null => (status === 'success' ? 'completed' : status);
